import { FilterOperators } from "@crispico/foundation-gwt-js";
import { EntityDescriptor, EntityTablePage, EntityTablePageProps, EntityTablePageReducers, Utils, FieldDescriptor, EntityEditorPage, EntityEditorPageProps } from "@crispico/foundation-react";
import { Filter } from "@crispico/foundation-react/components/CustomQuery/Filter";
import { TabRouterPane } from "@crispico/foundation-react/components/TabbedPage/TabbedPage";
import { FieldType } from "@crispico/foundation-react/entity_crud/FieldType";
import { HumanResourceScheduleTeamsPageRRC } from "./HumanResourceScheduleTeamsPage";
import { AssociationStringFieldEditorFieldDescriptor } from "components/AssociationStringFieldEditor";
import { MissionTotalTimeFieldDescriptor } from "pages/CommonFieldDescriptors";
import { ReduxReusableComponents, RRCProps } from "@crispico/foundation-react/reduxReusableComponents/ReduxReusableComponents";
import lodash from "lodash";
import moment from "moment-timezone";
import { GanttUtils } from "pages/gantt/GanttUtils";
import { TIME_HOUR_OFFSET } from "pages/gantt/XopsGantt";
import { Group, Item, RowLayer } from '@crispico/react-timeline-10000';
import { apolloClient } from "@crispico/foundation-react";
import { FindByFilterParams } from "@crispico/foundation-react/entity_crud/FindByFilterParams";
import gql from "graphql-tag";
import { DatePickerFieldEditor } from "@crispico/foundation-react/entity_crud/fieldEditors/DatePickerFieldEditor";
import DateFieldRenderer from "@crispico/foundation-react/entity_crud/fieldRenderers/DateFieldRenderer";
import { getLayerStyle } from "pages/gantt/ganttLayerStyles";
import { HumanResourceSchedule, Mission } from "../../types";
import { XopsGanttPartialProps, EntityTablePageWithGantt, EntityTablePageWithGanttOptions, EntityTablePageWithGanttState } from "pages/gantt/components/tables/EntityTablePageWithGantt";
import { getEntityGraphFields } from "@crispico/foundation-react/utils/GraphqlUtils";

export const HRS_OPTIONS_LOCAL_STORAGE_ITEM = "HumanResourceScheduleTablePage.options";

export class HumanResourceScheduleEntityDescriptor extends EntityDescriptor {
    constructor() {
        super({
            name: "HumanResourceSchedule",
            miniFields: ["humanResource.identifier", "startTime"],
            icon: "group",
            defaultSort: { field: "startTime", direction: "ASC" },
        });
    }

    protected customize() {
        this.addFieldDescriptor({ name: "team", type: FieldType.string }, AssociationStringFieldEditorFieldDescriptor("HumanResource"))
            .addFieldDescriptor({ name: "startTime", type: FieldType.date, additionalFieldEditorProps: additionalDateFieldEditorProps, additionalFieldRendererProps: additionalDateFieldRendererProps })
            .addFieldDescriptor({ name: "endTime", type: FieldType.date, additionalFieldEditorProps: additionalDateFieldEditorProps, additionalFieldRendererProps: additionalDateFieldRendererProps })
            .addFieldDescriptor({ name: "finishedDate", type: FieldType.date, additionalFieldEditorProps: additionalDateFieldEditorProps, additionalFieldRendererProps: additionalDateFieldRendererProps })
            .addFieldDescriptor(new MissionTotalTimeFieldDescriptor())
            // TODO CC #39307: work in progress 
            // .addFieldDescriptor(
            //     { name: "humanResourceMissionEventMessages", type: FieldType.string, clientOnly: true },
            //     new class extends FieldDescriptor {
            //         getAppearsInUi() {
            //             return true;
            //         }
            //         protected renderFieldInternal(RendererClass: any, props: any, entity: any) {
            //             return React.createElement(MissionEventMessagesFieldRenderer, props);
            //         }
            //     }()
            // )
            // it doesn't work on a table/editor page, was used only in gantt for get missions using the proxy 
            .addFieldDescriptor({ name: "missions", type: FieldType.oneToMany("Mission2"), oneToManyOppositeField: "humanResource", oneToManyEntityField: "humanResource", enable: false, filterable: false, sortable: false, isInDefaultColumnConfig: false, clientOnly: true })
        this.isInDefaultColumnConfig(false, "humanResourceId", "finished", "units", "autoPauseProcessed", "equipment", "radio", "driverAssistant1Id", "driverAssistant2Id");

        this.infoEditor.wrappedComponentClass = class extends EntityEditorPage<EntityEditorPageProps> {

            protected getExtraTabPanes(): (TabRouterPane | null)[] {
                return [
                    ...super.getExtraTabPanes(),
                    this.createOneToManyTabPane("Mission2", "humanResource", { entityField: "humanResourceId" })
                ];
            }
        }
    }

    renderTable() {
        return <HumanResourceScheduleTablePageRRC {...super.renderTable().props} ref={this.entityTablePage} />;
    }
}

export interface HumanResourceScheduleTablePageOptions extends EntityTablePageWithGanttOptions {
    hideFinishedSchedules: boolean
}

const additionalDateFieldEditorProps = FieldDescriptor.castAdditionalFieldEditorProps(DatePickerFieldEditor, { format: Utils.dateTimeFormat });
const additionalDateFieldRendererProps = FieldDescriptor.castAdditionalFieldRendererProps(DateFieldRenderer, { format: Utils.dateTimeFormatShorter, formatForToday: Utils.timeFormat });

const humanResourceScheduleTablePageOptionsEntityDescriptor = new EntityDescriptor({ name: "HumanResourceScheduleTablePageOptions" })
    .addFieldDescriptor({ name: "showGantt", type: FieldType.boolean })
    .addFieldDescriptor({ name: "compactMode", type: FieldType.boolean })
    .addFieldDescriptor({ name: "hideFinishedSchedules", type: FieldType.boolean })
    .addFieldDescriptor({ name: "date", type: FieldType.date, additionalFieldEditorProps: FieldDescriptor.castAdditionalFieldEditorProps(DatePickerFieldEditor, { format: Utils.dateFormat }), additionalFieldRendererProps: additionalDateFieldRendererProps })
// Variant for embedded mode: date field disabled in editor
const humanResourceScheduleTablePageOptionsEntityDescriptorEmbedded = new EntityDescriptor({ name: "HumanResourceScheduleTablePageOptions" })
    .addFieldDescriptor({ name: "showGantt", type: FieldType.boolean })
    .addFieldDescriptor({ name: "compactMode", type: FieldType.boolean })
    .addFieldDescriptor({ name: "hideFinishedSchedules", type: FieldType.boolean })
    .addFieldDescriptor({ name: "date", type: FieldType.date, enabled: false, additionalFieldEditorProps: FieldDescriptor.castAdditionalFieldEditorProps(DatePickerFieldEditor, { format: Utils.dateFormat }), additionalFieldRendererProps: additionalDateFieldRendererProps })

export class HumanResourceScheduleTablePageState extends EntityTablePageWithGanttState<HumanResourceScheduleTablePageOptions> {
    protected static readonly defaultOptions: HumanResourceScheduleTablePageOptions = {
        ...EntityTablePageWithGanttState.defaultOptions,
        hideFinishedSchedules: true,
    };
    protected static readonly localStorageKey: string = 'HumanResourceScheduleTablePage.options';

    missionsByHrId: { [hrId: number]: Mission[] } = {};
}

export class HumanResourceScheduleTablePageReducers<S extends HumanResourceScheduleTablePageState = HumanResourceScheduleTablePageState> extends EntityTablePageReducers<S> {
}

export type HumanResourceScheduleTablePageProps = { getFullEntity?: (entityName: string, entity: any) => any } & EntityTablePageProps & RRCProps<HumanResourceScheduleTablePageState, HumanResourceScheduleTablePageReducers>;

export class HumanResourceScheduleTablePage<P extends HumanResourceScheduleTablePageProps = HumanResourceScheduleTablePageProps> extends EntityTablePageWithGantt<P> {

    static defaultProps = {
        ...EntityTablePage.defaultProps
    }

    async componentDidUpdateInternal(prevProps?: P) {
        super.componentDidUpdateInternal(prevProps);
        if (prevProps && !lodash.isEqual(prevProps.s.loaded, this.props.s.loaded) && this.props.s.loaded !== -1) {
            this.loadMissions();
        }
    }

    protected getGanttStartEndTimes() {
        const date = this.props.s.options.date || Utils.now().toISOString();
        return [moment(date).startOf("day").add(-TIME_HOUR_OFFSET, "hour"), moment(date).endOf("day").add(TIME_HOUR_OFFSET, "hour")];
    }

    adjustFilterBeforeLoad(filter: Filter): Filter {
        filter = super.adjustFilterBeforeLoad(filter);
        const filterWithDate = this.props.s.options.date
            ? Filter.createComposed(FilterOperators.forComposedFilter.and, [filter, Filter.create("startTime", FilterOperators.forDate.dayOf, this.props.s.options.date)])
            : filter;
        const filterWithShowFinishedOption = this.props.s.options.hideFinishedSchedules
            ? Filter.createComposed(FilterOperators.forComposedFilter.and, [filterWithDate,
                Filter.createComposed(FilterOperators.forComposedFilter.or, [Filter.create("finished", FilterOperators.forBoolean.equals, "false"), Filter.create("finished", FilterOperators.forBoolean.isEmpty)])])
            : filterWithDate;
        return filterWithShowFinishedOption;
    }

    protected async loadMissions(list?: HumanResourceSchedule[]) {
        console.log("loadMissions");
        if (!this.props.s.options.showGantt) {
            return;
        }
        const entities = list || this.entityTableSimpleRef.current?.getEntities() as HumanResourceSchedule[];
        const ids = entities?.map(e => e.humanResource.id);
        if (Utils.isNullOrEmpty(ids)) {
            return;
        }
        const filters = [
            Filter.create("humanResource", FilterOperators.forNumber.in, ids!.join(",")),
            Filter.createComposed(FilterOperators.forComposedFilter.and, [
                Filter.create("endTime", FilterOperators.forDate.greaterThan, this.getGanttStartEndTimes()[0].toISOString()),
                Filter.create("startTime", FilterOperators.forDate.lessThan, this.getGanttStartEndTimes()[1].toISOString())
            ])
        ];
        const tasksPart = this.props.embeddedMode ? "" : " tasks { id actualStartTime actualEndTime comment taskType { name color } taskGroup { id name departure airline number date initialDate comment color groundAgent } }";
        
        const fragment = `  id type { id color name } inactivityType {id color name } humanResource { id } startTime endTime comment${tasksPart}
                        status finishedByUser color startAddress { id name } endAddress { id name } equipmentResource { id identifier } equipmentType { id name }
                        events { id creationdate driverId generationSource stopInfo flight objectActionInfo descriptorType deleted status notificationCategory message driver userName statusChangedAt statusChangedBy observation comment type}`;
        
        const query = gql(`query q($params: FindByFilterParamsInput) { 
                mission2Service_findByFilter(params: $params) {
                    results {
                      ${fragment}
                    }
                }
            }`);
        const missions = (await apolloClient.query({
            query: query,
            variables: FindByFilterParams.create().filter(Filter.createComposed(FilterOperators.forComposedFilter.and, filters)).entityGraph(getEntityGraphFields(fragment)),
            context: { showSpinner: false }
        })).data.mission2Service_findByFilter.results;
        const missionsByHrId: { [hrId: number]: Mission[] } = {};
        for (const m of missions as Mission[]) {
            const hrId = m.humanResource?.id;
            if (!hrId) { continue; }
            (missionsByHrId[hrId] = missionsByHrId[hrId] || []).push(m);
        }
        this.props.r.setInReduxState({ missionsByHrId });
    }

    protected getRowLayers(): RowLayer[] {
        const rowLayers: RowLayer[] = [];
        const entities = this.entityTableSimpleRef.current?.getEntities() as HumanResourceSchedule[];
        entities?.map((e, idx) => {
            rowLayers.push({
                rowNumber: idx,
                start: e.startTime,
                end: e.endTime,
                style: getLayerStyle("HumanResourceSchedule", e)
            });
        });
        return rowLayers;
    }

    protected getFullMission(m: Mission) {
        return this.props.getFullEntity ? this.props.getFullEntity("Mission", m) : m;
    }

    protected getTimelineData(): { groups: Group[], items: (Item & { entity: any })[], rowLayers: RowLayer[] } {
        const groups: Group[] = [];
        const items: (Item & { entity: any })[] = [];
        const rowLayers: RowLayer[] = [];
        const entities = this.entityTableSimpleRef.current?.getEntities();
        // TODO CC: I use the HR id to link the group/row to the items -> KO when I have 2 HRS for a single HR!
        entities?.forEach((entity, index) => {
            groups.push({ id: entity.humanResource.id });
            const missions = (this.props.s as HumanResourceScheduleTablePageState).missionsByHrId?.[entity.humanResource.id] || [];
            missions.forEach((m: Mission) => {
                items.push({
                    key: GanttUtils.toEntityUid("Mission2", m.id),
                    row: m.humanResource.id,
                    start: m.startTime,
                    end: m.endTime,
                    entity: this.getFullMission(m)
                });
            });
            rowLayers.push({
                rowNumber: index,
                start: entity.startTime,
                end: entity.endTime,
                style: getLayerStyle("HumanResourceSchedule", entity)
            });
        });
        return { groups, items, rowLayers };
    }

    protected getExtraTabPanes(): (TabRouterPane | null)[] {
        return [humanResourceScheduleTeamsPageTab, ...super.getExtraTabPanes()];
    }

    protected getOptionsEntityDescriptor(): EntityDescriptor {
        return this.props.embeddedMode ? humanResourceScheduleTablePageOptionsEntityDescriptorEmbedded : humanResourceScheduleTablePageOptionsEntityDescriptor;
    }

    protected getXopsGanttProps(): XopsGanttPartialProps {
        const { groups, items, rowLayers } = this.getTimelineData();
        return {
            startDate: this.getGanttStartEndTimes()[0].valueOf(),
            endDate: this.getGanttStartEndTimes()[1].valueOf(),
            groups, items, rowLayers,
        }
    }
};

export const HumanResourceScheduleTablePageRRC = ReduxReusableComponents.connectRRC(HumanResourceScheduleTablePageState, HumanResourceScheduleTablePageReducers, HumanResourceScheduleTablePage);

export const humanResourceScheduleTeamsPageTab = {
    routeProps: { path: "/teamsPage" },
    menuItemProps: { icon: "group", content: _msg("HumanResourceScheduleTeams.title") },
    render: () => <HumanResourceScheduleTeamsPageRRC id="humanResourceScheduleTable_teamsPage" />
};


